import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  CancellationReasons,
  ORDER_STATUS_CLASSNAME,
  OrderStateStatus,
  PRODUCT_PLACEHOLDER_URL,
} from '../../constants';
import { APP_SELLER_ROUTE, BUY_AGAIN } from '~shared/constants';
import { METRO_ID_ORGANIZATION } from '~shared/constants/metro-seller.constant';
import { Money } from '~shared/models';
import { OrderLine } from '../../models';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { debounceTime, share } from 'rxjs/operators';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { OrderHistoryApplicationEvent, } from '~shared/modules/application-event/models';
import { GaTrackingEventCategory, GaTrackingEventName } from '~shared/modules/ga-tracking/constants';
import { OrderHistoryEventActionType } from '~shared/modules/application-event/constants';
import { TranslateService } from '@ngx-translate/core';
import { OrdersHistoryService } from '../../services/orders-history.service';
import { APP_PRODUCT_ROUTE } from '~app/modules/account/modules/orders-history/constants/routes.constant';
import { PaymentStatus } from '~app/modules/account/modules/orders-history/models/payment-status.enum';


@Component({
  selector: 'app-order-lines-item',
  templateUrl: './order-lines-item.component.html',
  styleUrls: ['./order-lines-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderLinesItemComponent implements OnInit, OnDestroy {
  @Input() orderLine: OrderLine;
  @Input() isEntrepreneur: boolean;
  @Input() orderCreationDate: string;
  @Input() orderId: string;
  @Input() orderNumber: string;
  @Input() isContactEmailFeatureEnabled: boolean;
  @Input() isGetInvoiceFeatureEnabled: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() isLastOrderLine: boolean;
  @Input() isBuyAgainButtonEnable: boolean;
  @Input() isSubsidiaryAccount: boolean;
  APP_PRODUCT_ROUTE = APP_PRODUCT_ROUTE;

  public unitPrice: Money;
  public price: Money;
  public shippingCost: Money;
  public totalPriceGross: Money;
  public totalPriceNet: Money;
  public discount: Money;
  public grandTotal: Money;

  public readonly productPlaceholderUrl = PRODUCT_PLACEHOLDER_URL();
  public readonly sellerRoute = `/${APP_SELLER_ROUTE}`;
  public readonly buyAgainRoute = `/${BUY_AGAIN}`;
  public readonly orderStatus = OrderStateStatus;
  public readonly statusClassName = ORDER_STATUS_CLASSNAME;
  public readonly cancellationReasons = CancellationReasons;
  public readonly metroIdOrganization = METRO_ID_ORGANIZATION;
  private destroy$ = new Subject<void>();

  public get isReturnRequested(): boolean {
    return this.orderLine.status.value === OrderStateStatus.RETURN_REQUESTED;
  }

  public get isReturnAccepted(): boolean {
    return this.orderLine.status.value === OrderStateStatus.RETURN_ACCEPTED;
  }

  public get isReturnRejected(): boolean {
    return this.orderLine.status.value === OrderStateStatus.RETURN_DECLINED;
  }

  public get isCancelled(): boolean {
    return this.orderLine.status.value === OrderStateStatus.CANCELED;
  }

  public get isWaitingForPayment(): boolean {
    return this.orderLine.status.value === OrderStateStatus.WAITING_FOR_PAYMENT;
  }
  public get isFailed(): boolean {
    return this.orderLine.status.paymentStatus === PaymentStatus.Failed;
  }

  public get isEnabledReturnReason(): boolean {
    return (
      this.isReturnRequested ||
      this.isReturnAccepted ||
      this.isReturnRejected
    );
  }

  public get isDiscountApplied(): boolean {
    const discount = +this.orderLine?.discount.amount;
    return discount > 0;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private applicationEventService: ApplicationEventService,
    private ordersHistoryService: OrdersHistoryService,
    private translateSrv: TranslateService
  ) {
  }

  ngOnInit() {
    this.setProductData();
    this.subscribeToWindowResize();
  }

  // Used to show tooltip on product name when name is truncated
  private subscribeToWindowResize() {
    const resizeDebounceTime = 250;
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(resizeDebounceTime),
        share(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setProductData(): void {
    const { unitPrice, price, shippingCost, totalPriceNet, totalPriceGross, discount, grandTotal } = this.orderLine;
    this.unitPrice = unitPrice;
    this.price = price;
    this.shippingCost = shippingCost;
    this.totalPriceNet = totalPriceNet;
    this.totalPriceGross = totalPriceGross;
    this.discount = discount;
    this.grandTotal = grandTotal;
  }

  public navigateToSellerClicked() {
    this.applicationEventService.publish(new OrderHistoryApplicationEvent({
      event: GaTrackingEventName.NavigateToSeller,
      action: OrderHistoryEventActionType.NavigateToSeller,
      label: this.orderLine.seller.shopName,
      category: GaTrackingEventCategory.Navigation,
    }));
  }

  public createSupportTicket(orderLineId: string): void {
    this.ordersHistoryService.createSupportTicket(orderLineId);
    this.applicationEventService.publish(new OrderHistoryApplicationEvent({
      event: GaTrackingEventName.ContactSeller,
      action: OrderHistoryEventActionType.ContactSeller,
      label: 'Order History Create Support Ticket',
    }));
  }

  public sendGAEventBuyAgain(): void {
    this.applicationEventService.publish(new OrderHistoryApplicationEvent({
      event: GaTrackingEventName.BuyAgain,
      action: OrderHistoryEventActionType.BuyAgain,
      label: this.orderLine?.product?.name,
    }));
  }

  public contactSeller(emailType: string): void {
    const subject = this.translateSrv.instant(`ORDER_HISTORY.ORDER_LINE.${emailType}.EMAIL_SUBJECT`)
      .replace('ORDER_NUMBER', this.orderNumber);
    const emailBody = this.translateSrv.instant(`ORDER_HISTORY.ORDER_LINE.${emailType}.EMAIL_BODY`)
      .replace('ORDER_NUMBER', this.orderNumber);
    window.location.href = `mailto:${this.orderLine.seller.email}?subject=${subject}&body=${emailBody}`;

    this.applicationEventService.publish(new OrderHistoryApplicationEvent({
      event: GaTrackingEventName.ContactSeller,
      action: OrderHistoryEventActionType.ContactSeller,
      label: 'Order History Seller Contact',
    }));

  }

}
